import _ from 'lodash';
import moment from 'moment';

export const programHasAppointment = scheduling =>
  scheduling.length > 0 && scheduling.filter(schedule => schedule.appointment.length > 0).length > 0;

export const programHasExpressionOfInterest = scheduling =>
  scheduling.length > 0 &&
  scheduling.filter(schedule => schedule.expression_of_interest_for_student_application.length > 0).length > 0;

export const applySchedulingOk = scheduling =>
  scheduling.length === 0 || programHasAppointment(scheduling) || programHasExpressionOfInterest(scheduling);

export const canApplyToProgram = program => applySchedulingOk(program.scheduling);

export const hasSavedPrograms = normalSchools => normalSchools.result.length > 0;

// blatant rip from
// https://github.com/tylerjpeterson/ios-inner-height
export const windowVerticalHeight = () => {
  if (!navigator.userAgent.match(/iphone|ipod|ipad/i)) {
    return window.innerHeight;
  }

  const axis = Math.abs(window.orientation);
  const dims = { w: 0, h: 0 };

  const createRuler = () => {
    let ruler = document.createElement('div');

    ruler.style.position = 'fixed';
    ruler.style.height = '100%';
    ruler.style.width = 0;
    ruler.style.top = 0;

    document.documentElement.appendChild(ruler);

    // Set cache conscientious of device orientation
    dims.w = axis === 90 ? ruler.offsetHeight : window.innerWidth;
    dims.h = axis === 90 ? window.innerWidth : ruler.offsetHeight;

    // Clean up after ourselves
    document.documentElement.removeChild(ruler);
    ruler = null;
  };

  createRuler();

  if (Math.abs(window.orientation) !== 90) {
    return dims.h;
  }
  return dims.w;
};

export const processShortCodeToName = ({ admissionProcess }) => {
  const lookup = {
    '2K': '2-k',
    '3K': '3k',
    K: 'kindergarten',
    PK: 'pre-k',
    MS: 'middle-school',
    HS: 'high-school',
    SHS: 'high-school',
    EL: 'early-learn',
    GTAPP: 'gt-app',
    SR: 'summer-rising',
    D75: 'district-75',
  };

  return lookup[admissionProcess] || null;
};

export const processNameToShortCode = ({ processName }) => {
  const lookup = {
    '2-k': '2K',
    '3k': '3K',
    kindergarten: 'K',
    'pre-k': 'PK',
    'middle-school': 'MS',
    'high-school': 'HS',
    'gt-app': 'GTAPP',
    'summer-rising': 'SR',
    'district-75': 'D75',
  };

  return lookup[processName] || null;
};

export const getPublicSchoolLink = ({
  //
  directorySchoolId,
  locale = 'en',
  admissionProcess = 'HS',
  dbn = '',
}) =>
  directorySchoolId
    ? `${window.location.protocol}//${window.location.host}/${locale}/schools/${processShortCodeToName({
        admissionProcess,
      })}/${dbn}`
    : null;

// This function should be used instead of intl.formatDate when passing date object (not datetime object)
// because otherwise it is possible that one day will be subtracted
export const formatDate = (date, intl, dateFormatOptions) => {
  const options = {};

  if (dateFormatOptions) {
    _.extend(options, dateFormatOptions);
  }

  options.timeZone = 'UTC';
  return intl.formatDate(date, options);
};

// Using the intl.formatDate function yields inconsistent times in safari. There appear to be timezone issues. Probably related to the UTC timezone.
export const formatTime = date => {
  let time = moment(date);
  time = time.format('h:mma');
  return time;
};

export const compareByTime = (firstTime, secondTime) => {
  if (moment(firstTime).isBefore(moment(secondTime))) {
    return -1;
  }
  if (moment(firstTime).isAfter(moment(secondTime))) {
    return 1;
  }
  return 0;
};

// Detects if an event is a keydown and either an enter or space bar press
export const detectActivationEvent = event => {
  if (event && event.type === 'keydown') {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return false;
    }
  }
  return true;
};

export const getSavedProgramCount = schools =>
  _.uniq(
    _.flatten(
      schools.map(school =>
        school.programs.result.filter(programId => school.programs.entities.program[programId].saved),
      ),
    ),
  ).length;

export const getUnappliedSavedPrograms = ({ normalSchools }) =>
  _.without(
    _.flatten(
      normalSchools.result.map(id =>
        normalSchools.entities.school[id].programs.result.map(programId => {
          if (
            !normalSchools.entities.school[id].programs.entities.program[programId].savedApplication &&
            normalSchools.entities.school[id].programs.entities.program[programId].saved
          ) {
            return {
              schoolId: id,
              programId,
              distance: parseFloat(normalSchools.entities.school[id].distance),
              savedAt: normalSchools.entities.school[id].programs.entities.program[programId].saved_at,
            };
          }
          return undefined;
        }),
      ),
    ),
    undefined,
  );

export const getWaitlistPrograms = ({ normalWaitlistSchools }) =>
  _.without(
    _.flatten(
      normalWaitlistSchools.result.map(id =>
        normalWaitlistSchools.entities.school[id].programs.result.map(programId => {
          if (normalWaitlistSchools.entities.school[id].programs.entities.program[programId].joinedWaitlist) {
            return {
              schoolId: id,
              programId,
              distance: parseFloat(normalWaitlistSchools.entities.school[id].distance),
            };
          }
          return undefined;
        }),
      ),
    ),
    undefined,
  );

export function generateUniqueId(props) {
  let id = '';

  Object.keys(props).forEach(prop => {
    id += `${props[prop]}-`;
  });

  id += `${Math.floor(Math.random() * 0xffff)}`;
  id = id.replace(/[^A-Za-z0-9-]/gi, '');
  return id;
}

export const elementIsInView = el => {
  const scroll = window.scrollY || window.pageYOffset;
  const boundsTop = el.getBoundingClientRect().top + scroll;

  const viewport = {
    top: scroll,
    bottom: scroll + window.innerHeight,
  };

  const bounds = {
    top: boundsTop,
    bottom: boundsTop + el.clientHeight,
  };

  return (
    (bounds.bottom >= viewport.top && bounds.bottom <= viewport.bottom) ||
    (bounds.top <= viewport.bottom && bounds.top >= viewport.top)
  );
};
// dead code
export function getPreKIconName(eligibility, otherFeatures) {
  const schoolTypeIcons = {
    'District School': 'districtSchool',
    NYCEEC: 'earlyEdCenter',
    'Charter School': 'charterSchool',
    'Pre-K Center': 'preKCenter',
    'Half Day': 'halfDayOnly',
    'Early Learn 0-3': 'earlyLearnThrees',
    'Family Child Care': 'familyChildCare',
  };

  if (eligibility && eligibility.length > 0 && eligibility[0].name != undefined) {
    const schoolType = eligibility.find(el => Object.keys(schoolTypeIcons).includes(el.name));
    if (schoolType) {
      return schoolTypeIcons[schoolType.name];
    }
  }

  if (otherFeatures && otherFeatures.length > 0 && otherFeatures[0].name != undefined) {
    const schoolType1 = otherFeatures.find(({ name }) => Object.keys(schoolTypeIcons).includes(name));
    if (schoolType1) {
      return schoolTypeIcons[schoolType1.name];
    }
  }

  return 'default';
}

export function getMapIconName(eligibility, otherFeatures) {
  const schoolTypeIcons = {
    'District School': 'zoneEligibility',
    'District School - Click a program to see eligibility.': 'zoneEligibility',
    'Non-Zoned School': 'zoneEligibility',
    'Zoned School - Click a program to see eligibility.': 'zoneEligibility',
    Zoned: 'zoneEligibility',
    'Zoned School': 'zonedSchoolEligibility',
    NYCEEC: 'nyceecEligibility',
    'Charter School': 'charterEligibility',
    'Pre-K Center': 'preKCenterEligibility',
    'Half Day': 'halfdayEligibility',
    'Family Child Care': 'familyChildCareEligibility',
    'Citywide School': 'cityWalkEligibility',
    'Transfer School': 'transferEligibility',
    'Early Learn 0-3': 'infantEligibility',
    'Early Learn': 'infantEligibility',
    D75: 'd75Eligibility',
  };
  if (eligibility && eligibility.length > 0 && eligibility[0].name_en != undefined) {
    const schoolTypes = eligibility.filter(el => Object.keys(schoolTypeIcons).includes(el.name_en));
    if (schoolTypes.length > 1) {
      // more than 1 eligibility from defined eligibility icons
      return MAPICONS.MultipleEligibilityMapIcon;
    }
    if (schoolTypes.length == 1) {
      // only 1 eligibility from defined eligibility icons
      return schoolTypeIcons[schoolTypes[0].name_en];
    }
  }

  if (otherFeatures && otherFeatures.length > 0 && otherFeatures[0].name != undefined) {
    const schoolType1 = otherFeatures.find(({ name }) => Object.keys(schoolTypeIcons).includes(name));
    if (schoolType1) {
      return schoolTypeIcons[schoolType1.name];
    }
  }
  return 'defaultEligibility';
}

// This will check if constance config has any dbn set for excluding guaranteed offer tooltip display
export function hideGuaranteedOfferExcludedSchools(dbn){
  const guaranteed_excluded_dbns = window.App.state.user.EXCLUDE_GUARANTEED_OFFER_SCHOOL_DBNS.split(',')
  return guaranteed_excluded_dbns.indexOf(dbn) > -1;
}

export const isEL = ({ user }) => user.admissionProcess === 'EL';
export const is2K = ({ user }) => user.admissionProcess === '2K';
export const is3K = ({ user }) => user.admissionProcess === '3K';
export const isK = ({ user }) => user.admissionProcess === 'K';
export const isSR = ({ user }) => user.admissionProcess === 'SR';
export const isDR = ({ user }) => user.admissionProcess === 'DR';
export const isPK = ({ user }) => user.admissionProcess === 'PK';
export const isMS = ({ user }) => user.admissionProcess === 'MS';
export const isHS = ({ user }) => user.admissionProcess === 'HS';
export const isSHS = ({ user }) => user.admissionProcess === 'SHS';
export const isLG = ({ user }) => user.admissionProcess === 'LG';
export const isGT = ({ user }) => user.admissionProcess === 'GT';
export const isGTAPP = ({ user }) => user.admissionProcess === 'GTAPP';
export const isNS = ({ user }) => user.admissionProcess === 'NS';
export const isWaitlist = ({ user }) => user.isWaitlist;
export const MAPICONS = {
  MultipleEligibilityMapIcon: 'multipleEligibilities',
  SpecializedSchoolsIcon: 'specializedEligibility',
  AffiliatedSchoolsIcon: 'affiliatedSchools',
  ZonedSchoolIcon: 'zonedSchoolEligibility',
  OverwriteImages: { Favorite: 'favorite', Applied: 'applied' },
};

export const customisedIntlFormatMessage = (intl, msgObj, valObj={}) => {
  /**
   * Customised function to handle html tags inside default messages for intl.formatMessage.
   */
  return intl.formatMessage(msgObj, {
    'p': (chunks) => `<p>${chunks}</p>`,
    'strong': (chunks) => `<strong>${chunks}</strong>`,
    ...valObj
  })
};
export const getCsrfToken = () => document.querySelector('meta[name="csrf-token"]').getAttribute('content');

export function isZonedSchool(dbn, admissionProcess) {
  const allowedZonedAdmission = ['3K', 'PK', 'K', 'GTAPP', 'HS', 'MS'];
  const zonedSchoolDbns = window.App.state.user.get_zoned_school_ids || [];
  const currentAdmission = (window.App.state.user.admissionProcess || admissionProcess).toUpperCase();
  return zonedSchoolDbns.includes(dbn) && allowedZonedAdmission.includes(currentAdmission);
}
